import { Icon } from '@/lib/icons';
import { AfricanKeyboard } from '@/components/keyboard';
import type { WordItem } from '../types';
import { getPreferredTranslation } from '../utils';

type NavigateTranslationPayload = {
  mode: 'toFrench' | 'toLocal' | 'editPair';
  translation?: string;
  localPhrase?: string;
};

type TranslationActionsCopy = Record<
  | 'translationLabel' | 'noFrenchTranslation' | 'editAddFrenchTranslation' | 'addFrenchTranslation'
  | 'noLocalTranslation' | 'editTranslatePhrase' | 'translatePhrase' | 'optionalHint'
  | 'editPhraseAndTranslation' | 'editLocalPhrase' | 'hideKeyboard' | 'openAfricanKeyboard' | 'save' | 'cancel',
  string
>;

type SpeakPhraseTranslationActionsProps = {
  t: TranslationActionsCopy;
  wordItem: WordItem;
  isModerator: boolean;
  isEditingLocal: boolean;
  editingLocalText: string;
  showAfricanKeyboard: boolean;
  selectedLanguage: string;
  onNavigateTranslation: (payload: NavigateTranslationPayload) => void;
  onStartLocalEdit: (value: string) => void;
  onEditingLocalTextChange: (value: string) => void;
  onToggleAfricanKeyboard: () => void;
  onSaveLocalEdit: () => void;
  onCancelLocalEdit: () => void;
};

export function SpeakPhraseTranslationActions({
  t,
  wordItem,
  isModerator,
  isEditingLocal,
  editingLocalText,
  showAfricanKeyboard,
  selectedLanguage,
  onNavigateTranslation,
  onStartLocalEdit,
  onEditingLocalTextChange,
  onToggleAfricanKeyboard,
  onSaveLocalEdit,
  onCancelLocalEdit,
}: SpeakPhraseTranslationActionsProps) {
  const translation = getPreferredTranslation(wordItem.translations);
  const hasLocalText = !!wordItem.text && wordItem.text.trim().length > 0;
  const hasFrenchTranslation = !!translation;
  const needsFrenchTranslation = hasLocalText && !translation;
  const needsLocalTranslation = !hasLocalText && hasFrenchTranslation;

  return (
    <div className="space-y-3 text-left">
      {translation && (
        <div className="rounded-xl border border-border/40 bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{t.translationLabel}</span> {translation}
        </div>
      )}

      {needsFrenchTranslation && (
        <TranslationNotice
          message={t.noFrenchTranslation}
          buttonLabel={isModerator ? t.editAddFrenchTranslation : t.addFrenchTranslation}
          onClick={() => onNavigateTranslation({ mode: 'toFrench', localPhrase: wordItem.text })}
        />
      )}

      {needsLocalTranslation && (
        <TranslationNotice
          message={t.noLocalTranslation}
          buttonLabel={isModerator ? t.editTranslatePhrase : t.translatePhrase}
          hint={t.optionalHint}
          onClick={() => onNavigateTranslation({ mode: 'toLocal', translation })}
        />
      )}

      {isModerator && hasLocalText && hasFrenchTranslation && (
        <button
          type="button"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full sm:w-auto border border-border/40 bg-background/60 px-4 py-2 text-xs font-semibold text-primary transition-all hover:border-primary/30 hover:bg-primary/8"
          onClick={() => onNavigateTranslation({ mode: 'editPair', localPhrase: wordItem.text, translation })}
        >
          <Icon name="edit" className="h-3.5 w-3.5" />
          {t.editPhraseAndTranslation}
        </button>
      )}

      {isModerator && hasLocalText && (
        <div className="mt-3">
          {!isEditingLocal ? (
            <button
              type="button"
              className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-border/40 bg-background/60 px-3 text-[0.68rem] font-semibold text-muted-foreground transition-all hover:border-border/60 hover:text-foreground"
              onClick={() => onStartLocalEdit(wordItem.text || '')}
            >
              {t.editLocalPhrase}
            </button>
          ) : (
            <div className="space-y-3 rounded-xl border border-border/40 bg-muted/20 p-3">
              <textarea
                className="w-full rounded-xl border border-border/50 bg-background/70 p-3 text-sm outline-none focus:border-primary/35"
                value={editingLocalText}
                onChange={(e) => onEditingLocalTextChange(e.target.value)}
                rows={3}
              />
              <button
                type="button"
                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-border/40 bg-background/60 px-3 text-[0.68rem] font-semibold text-muted-foreground transition-all hover:border-border/60 hover:text-foreground"
                onClick={onToggleAfricanKeyboard}
              >
                {showAfricanKeyboard ? t.hideKeyboard : t.openAfricanKeyboard}
              </button>
              {showAfricanKeyboard && (
                <AfricanKeyboard
                  language={selectedLanguage || 'generic'}
                  value={editingLocalText}
                  onChange={onEditingLocalTextChange}
                  onClose={onToggleAfricanKeyboard}
                />
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-border/40 bg-background/60 px-3 text-[0.68rem] font-semibold text-muted-foreground transition-all hover:border-border/60 hover:text-foreground"
                  onClick={onSaveLocalEdit}
                >
                  {t.save}
                </button>
                <button
                  type="button"
                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-full border border-border/40 bg-background/60 px-3 text-[0.68rem] font-semibold text-muted-foreground transition-all hover:border-border/60 hover:text-foreground"
                  onClick={onCancelLocalEdit}
                >
                  {t.cancel}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TranslationNotice({ message, buttonLabel, hint, onClick }: { message: string; buttonLabel: string; hint?: string; onClick: () => void }) {
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/[0.06] p-3">
      <p className="mb-2 text-sm text-foreground/85">{message}</p>
      <button
        type="button"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full sm:w-auto border border-border/40 bg-background/60 px-4 py-2 text-xs font-semibold text-foreground transition-all hover:border-border/60"
        onClick={onClick}
      >
        <Icon name="edit" className="h-3.5 w-3.5" />
        {buttonLabel}
        <Icon name="externalLink" className="h-3 w-3" />
      </button>
      {hint && <p className="mt-2 text-center text-xs text-muted-foreground/60">{hint}</p>}
    </div>
  );
}

export type { NavigateTranslationPayload, SpeakPhraseTranslationActionsProps };
